import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { ClassAttributes, createElement, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import ProductPricing from '../../../entity/ProductPricing';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import mq from '../../styleUtility/mediaquery';
import _PointSubTotal from './../compound/PointSubTotal';
import RequiredPointSummaryDescription from './RequiredPointSummaryDescription';

interface Props extends ClassAttributes<HTMLElement> {
  currentPointBalance: number;
  pointTotal: number;
  productPricing?: ProductPricing;
  className?: string;
}

const RequiredPointSummary = ({ currentPointBalance, pointTotal, productPricing, className }: Props) => {
  let depositsTotal = 0;
  let subtotal = 0;
  let taxSubtotal = 0;
  let depositsUsage = 0;
  let depositsSubtotal = 0;
  let orderTotal = 0;

  if (productPricing) {
    if (productPricing.taxRate > 0) {
      depositsTotal = currentPointBalance
        ? currentPointBalance * productPricing.flatRate * (1 + productPricing.taxRate)
        : 0;
    } else {
      depositsTotal = currentPointBalance ? currentPointBalance * productPricing.flatRate : 0;
    }
    subtotal = pointTotal * productPricing.flatRate;
    taxSubtotal = subtotal * productPricing.taxRate;
    depositsUsage = subtotal + taxSubtotal - depositsTotal;
    depositsSubtotal = depositsUsage <= 0 ? subtotal + taxSubtotal : depositsTotal;
    orderTotal = depositsUsage <= 0 ? 0 : depositsUsage;
  }

  return (
    <Root className={className}>
      <Row>
        {productPricing ? (
          <>
            <Column>
              <Label>
                <FormattedMessage id="required_point_summary.point_total" />
              </Label>
              <Price>
                <Point>{productPricing.currency + subtotal.toLocaleString()}</Point>
              </Price>
            </Column>
          </>
        ) : null}
        {productPricing ? (
          <>
            <Column>
              <Label>
                <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.tax_total" />
              </Label>
              <Price>
                <Point>{productPricing.currency + taxSubtotal.toLocaleString()}</Point>
              </Price>
            </Column>
          </>
        ) : null}
        {depositsSubtotal > 0 && productPricing ? (
          <>
            <Column>
              <Label>
                <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.deposits_total" />
              </Label>
              <Price>
                <Large>
                  -{productPricing.currency + depositsSubtotal.toLocaleString()}
                  <Point variant="caption">(-{pointTotal.toLocaleString()}p)</Point>
                </Large>
              </Price>
            </Column>
          </>
        ) : null}
        {productPricing ? (
          <>
            <Column>
              <PriceTotalLabel>
                <FormattedMessage id="creation_set_list.creation_set_list_item.detail.column_name.order_total" />
              </PriceTotalLabel>
              <PriceTotal>{productPricing.currency + orderTotal.toLocaleString()}</PriceTotal>
            </Column>
          </>
        ) : null}
      </Row>
      {currentPointBalance > 0 && productPricing ? (
        <Row>
          <RequiredPointSummaryDescription pointBalance={currentPointBalance} productPricing={productPricing} />
        </Row>
      ) : null}
    </Root>
  );
};

const Root = styled('div')(
  {
    paddingTop: SPACING_REGULAR,
    paddingBottom: SPACING_REGULAR,
  },
  {
    [mq('sp')]: {
      paddingTop: 0,
      paddingBottom: 0,
      marginRight: 0,
    },
  },
);

const Row = styled('div')(
  flexibleLayoutContainer({
    direction: 'column',
    justifyContent: 'flex-end',
  }),
  {
    marginBottom: SPACING_REGULAR,
    ':last-child': {
      marginBottom: 0,
    },
    marginRight: SPACING_UNIT,
  },
  {
    [mq('sp')]: {
      display: 'block',
    },
  },
);

const Column = styled('div')(
  gridLayoutContainer({
    columns: ['1fr', '140px', '140px'],
    rows: ['48px'],
    areas: [['.', 'point_label', 'point_subtotal']],
    alignItems: 'flex-start',
  }),
  {
    minWidth: 160,
    textAlign: 'right',
    [mq('sp')]: {
      ...flexibleLayoutContainer({
        direction: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
      }),
      padding: `${SPACING_REGULAR}px ${SPACING_REGULAR}px`,
    },
  },
);

const Label = styled(Typography)(gridLayoutItem('point_label'), {
  fontWeight: 500,
});

const Price = styled('div')(gridLayoutItem('point_subtotal'), {
  fontWeight: 500,
});

const Large = styled('p')({
  fontSize: 16,
});

const Point = styled(Typography)({
  fontWeight: 'bold',
  [mq('sp')]: {
    textAlign: 'right',
  },
});

const PriceTotalLabel = styled(Typography)(gridLayoutItem('point_label'), {
  fontSize: 20,
  fontWeight: 'bold',
});

const PriceTotal = styled(Typography)(gridLayoutItem('point_subtotal'), {
  fontSize: 26,
  fontWeight: 700,
  [mq('sp')]: {
    textAlign: 'right',
  },
});
PriceTotal.defaultProps = { color: 'primary' };

export default RequiredPointSummary;
